import React, { memo, useMemo, PropsWithChildren } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { Locales } from '@amzn/xb-hooks';
import { ExperienceBridgeProvider } from '@amzn/aws-tc-xb-root-utils';
import { NotificationProvider } from './components';
import { COURSE_DETAIL_ROUTE, DEFAULT_ROUTE, HOME_ROUTE, MATCH_ALL_ROUTE, PROFILE_ROUTE } from './common/routes';
import { AccessDenied, Course, Dashboard, PageNotFound } from './pages';
import { CourseProvider } from './components/course/CourseContext';
import { LoadingPage } from './pages/loadingPage/LoadingPage';
import Profile from './pages/profile/Profile';
import { config } from './config/XBProviderConfig';
import { useUserInfo } from './hooks/user/useUserInfo';
import { MainLayout } from './components/Layouts/Layouts';
import { TrackedPage } from './components/TrackedPage';
import { ApolloProvider } from '@apollo/client';
import { client } from './common/client';
import { getXBEnvironment, getNodeEnvironment, NodeEnvironment } from './common';
import { AuthProvider } from './components/auth/AuthContext';

// Separate component to use hooks after ExperienceBridgeProvider is initialized
export const XBConfiguration = ({ children }: PropsWithChildren) => {
    const { loading, user } = useUserInfo();

    /** TODO - Need to check if we need to add authz */
    if (loading) {
        return <LoadingPage text='Loading...' />;
    }

    if (!user.id) {
        return <AccessDenied />;
    }

    return children;
};

type ConfigurationProps = PropsWithChildren<{
    xpProviderProps: Omit<React.ComponentProps<typeof ExperienceBridgeProvider>, 'children'>;
}>;

/**
 * Defined at module scope on purpose. Declaring this inside `AppWrapper` would mint a new component
 * type on every render, which makes `memo` inert and forces React to unmount and remount the whole
 * `ExperienceBridgeProvider` subtree — re-initializing XPS and refetching every remote component.
 */
const Configuration = memo(function Configuration({ children, xpProviderProps }: ConfigurationProps) {
    return (
        <div data-testid='xb-configuration'>
            <ExperienceBridgeProvider {...xpProviderProps}>
                <XBConfiguration>{children}</XBConfiguration>
            </ExperienceBridgeProvider>
        </div>
    );
});

const AppWrapper = ({ children }: PropsWithChildren) => {
    // Memoized so `Configuration`'s memo comparison can actually short-circuit; a fresh object
    // literal each render would defeat it and remount the provider.
    const xpProviderProps = useMemo(
        () => ({
            locale: Locales.ENGLISH,
            environment: getXBEnvironment(),
            config,
            ...(getNodeEnvironment() === NodeEnvironment.DEVELOPMENT && {
                localXpsConfig: [{ name: 'iltclassrooms', endpoint: 'http://localhost:3006' }],
            }),
        }),
        [],
    );

    return (
        <ApolloProvider client={client}>
            <AuthProvider>
                <Configuration xpProviderProps={xpProviderProps}>{children}</Configuration>
            </AuthProvider>
        </ApolloProvider>
    );
};

const App = () => {
    return (
        <BrowserRouter>
            <AppWrapper>
                <NotificationProvider>
                    <CourseProvider>
                        <Routes>
                            <Route element={<MainLayout isXbEnabled={true} />}>
                                <Route
                                    path={DEFAULT_ROUTE}
                                    element={
                                        <TrackedPage>
                                            <Dashboard />
                                        </TrackedPage>
                                    }
                                />
                                <Route
                                    path={HOME_ROUTE}
                                    element={
                                        <TrackedPage>
                                            <Dashboard />
                                        </TrackedPage>
                                    }
                                />
                                <Route
                                    path={COURSE_DETAIL_ROUTE}
                                    element={
                                        <TrackedPage>
                                            <Course />
                                        </TrackedPage>
                                    }
                                />
                                <Route
                                    path={PROFILE_ROUTE}
                                    element={
                                        <TrackedPage>
                                            <Profile />
                                        </TrackedPage>
                                    }
                                />
                                <Route path={MATCH_ALL_ROUTE} element={<PageNotFound />} />
                            </Route>
                        </Routes>
                    </CourseProvider>
                </NotificationProvider>
            </AppWrapper>
        </BrowserRouter>
    );
};

export default App;
